import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Loader2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field =
  "w-full bg-transparent border border-charcoal/20 rounded-xl px-4 py-2.5 text-charcoal placeholder:text-stone/50 focus:outline-none focus:border-clay transition-colors";

export const ManualAddDialog = ({ open, token, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", horse_name: "", yard_location: "",
    scheduled_at: "", status: "confirmed", message: "",
  });
  const [baseFee, setBaseFee] = useState(125);
  const [farmCall, setFarmCall] = useState(false);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const amount = Number(baseFee || 0) + (farmCall ? 50 : 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Please enter a name."); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/appointments`,
        { ...form, amount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCreated(data);
      toast.success("Booking added");
      setForm({ name: "", email: "", phone: "", horse_name: "", yard_location: "", scheduled_at: "", status: "confirmed", message: "" });
      setBaseFee(125); setFarmCall(false);
      onClose();
    } catch {
      toast.error("Could not add booking.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="manual-add-dialog" className="bg-sand border-charcoal/10 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-charcoal font-light">Add a booking</DialogTitle>
          <DialogDescription className="text-stone">For phone/walk-in clients who didn't book online. No email is sent.</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3 py-1">
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="manual-name" className={field} placeholder="Client name *" value={form.name} onChange={upd("name")} />
            <input data-testid="manual-phone" className={field} placeholder="Phone" value={form.phone} onChange={upd("phone")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="manual-email" className={field} placeholder="Email (optional)" value={form.email} onChange={upd("email")} />
            <input data-testid="manual-horse" className={field} placeholder="Horse's name" value={form.horse_name} onChange={upd("horse_name")} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="manual-yard" className={field} placeholder="Barn / location" value={form.yard_location} onChange={upd("yard_location")} />
            <input data-testid="manual-datetime" type="datetime-local" className={field} value={form.scheduled_at} onChange={upd("scheduled_at")} />
          </div>

          <div className="grid grid-cols-2 gap-3 items-center">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-clay">Session fee ($)</label>
              <input data-testid="manual-fee" type="number" min="0" step="5" className={`${field} mt-1`} value={baseFee} onChange={(e) => setBaseFee(e.target.value)} />
            </div>
            <label className="flex items-center gap-2 text-sm text-charcoal mt-5 cursor-pointer">
              <input data-testid="manual-farmcall" type="checkbox" checked={farmCall} onChange={(e) => setFarmCall(e.target.checked)} className="accent-clay w-4 h-4" />
              Travel over 1 hr (+$50)
            </label>
          </div>

          <div>
            <label className="text-xs uppercase tracking-[0.15em] text-clay">Status</label>
            <select data-testid="manual-status" className={`${field} mt-1`} value={form.status} onChange={upd("status")}>
              <option value="confirmed">Confirmed</option>
              <option value="completed">Completed (already done & paid)</option>
              <option value="new">New</option>
            </select>
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-sm text-stone">Total: <span className="font-heading text-xl text-charcoal">${amount}</span></p>
            <button
              type="submit"
              data-testid="manual-submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-clay text-sand hover:bg-clay-dark text-sm font-medium transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} strokeWidth={1.5} />}
              Add booking
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
